import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useSearchMyMonitoringAccountsCount, useSearchMyMonitoringAccountsInfinite } from '@apps-common/apis/portal/my-monitoring'
import { MyMonitoringAccountCardSkeleton, MyMonitoringAccountListCard } from '@apps-common/apps/portal/pages/my-monitoring/components'
import { EndOfResults, InfiniteLoader, NoResultsCard, PullRefresh, RootHeaderButtons } from '@apps-common/components/common'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { NO_RESULTS_DEFAULT_FILTER_MESSAGE } from '@apps-common/config/app-messages'
import { FILTER_DEFAULT_INFINITE_TAKE } from '@apps-common/config/filter'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { useMyMonitoringFilterStore } from '@apps-common/stores/useMyMonitoringFilterStore'
import { IMyMonitoringAccountListModel } from '@apps-common/types/portal/my-monitoring'
import { useMemo } from 'react'
import { MyMonitoringHeader } from '../MyMonitoringHeader'

export const MyMonitoringAccountInfiniteView: React.FC = () => {
  const { navigateForward } = useNavigation()
  const filter = useMyMonitoringFilterStore()

  const {
    isLoading: countIsLoading,
    isError: countIsError,
    data: countData
  } = useSearchMyMonitoringAccountsCount({
    search: filter.search,
    take: FILTER_DEFAULT_INFINITE_TAKE
  })

  const { isLoading, isError, data, isFetching, isRefetching, refetch, fetchNextPage, hasNextPage } = useSearchMyMonitoringAccountsInfinite(
    {
      enabled: true,
      search: filter.search,
      take: FILTER_DEFAULT_INFINITE_TAKE
    }
  )

  const reducedData = useMemo(() => {
    return data?.pages?.reduce((acc: IMyMonitoringAccountListModel[], curr: IMyMonitoringAccountListModel[]) => [...acc, ...curr], []) || []
  }, [data])

  const countLoading = countIsLoading || countIsError
  const loading = !data || isLoading || isRefetching || isError

  return (
    <>
      <MyMonitoringHeader
        icons={<RootHeaderButtons />}
        leftComponent={<PageResultsCount loading={countLoading} count={countData} />}
        rightComponent={<PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />}
        loading={countLoading}
        navigateForward={navigateForward}
        searchCount={countData?.resultCount || -1}
      />
      <LayoutContainer>
        <LayoutPadding topPadding='relative pt-[50px] md:pt-[70px]'>
          <PullRefresh loading={isRefetching} callback={() => refetch()} />
          <AnimatePresence>
            {loading && (
              <motion.div
                key={`my-monitoring-account-list-card-container-skeleton`}
                {...motionFadeIn(0.2)}
                className='grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[15px]'
              >
                {Array.from(Array(20).keys()).map((n, i) => (
                  <MyMonitoringAccountCardSkeleton key={`my-monitoring-account-card-skeleton-${i}`} />
                ))}
              </motion.div>
            )}
            {!loading && (
              <motion.div
                key={`my-monitoring-account-list-card-container`}
                {...motionFadeIn(0.2)}
                className='grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[15px]'
              >
                {reducedData.map((a) => (
                  <MyMonitoringAccountListCard key={`my-monitoring-account-list-${a.id}`} account={a} />
                ))}
              </motion.div>
            )}
            {!loading && reducedData.length === 0 && <NoResultsCard>{NO_RESULTS_DEFAULT_FILTER_MESSAGE}</NoResultsCard>}
            <EndOfResults key='my-monitoring-account-list-end-of-results' show={reducedData.length > 0 && !hasNextPage} />
          </AnimatePresence>
          <IonInfiniteScroll
            onIonInfinite={async (ev) => {
              await fetchNextPage()
              ev.target.complete()
            }}
          >
            <InfiniteLoader key='my-monitoring-account-list-infinite-loader' loading={isFetching} />
          </IonInfiniteScroll>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
